/**
 * QueryBuilder Store - pipeline slice.
 *
 * Project-level transformation pipelines: create/remove/rename/duplicate/
 * select, backend hydration + saved-name tracking. Step-level editing of
 * the active pipeline lives in stepSlice/loopSlice/weightingSlice.
 */
import { StateCreator } from "zustand"
import { TransformPipeline } from "types/queryBuilder.types"
import { PipelineSlice, QueryBuilderStore } from "../types"
import {
  blankPipeline,
  cloneStepsWithFreshIds,
  newId,
  nextPipelineName,
  normalizeHydratedSteps,
} from "../helpers"

export const createPipelineSlice: StateCreator<
  QueryBuilderStore,
  [["zustand/persist", unknown]],
  [],
  PipelineSlice
> = (set) => ({
  transformPipelines: [],
  activeTransformPipelineId: null,
  pipelineSavedNames: {},

  // ---- Pipeline management ----
  createTransformPipeline: (name) => {
    let createdId = ""
    set((state) => {
      const fresh = blankPipeline(
        name?.trim() || nextPipelineName(state.transformPipelines)
      )
      createdId = fresh.id
      return {
        transformPipelines: [...state.transformPipelines, fresh],
        activeTransformPipelineId: fresh.id,
      }
    })
    return createdId
  },
  removeTransformPipeline: (pipelineId) =>
    set((state) => {
      // Drop the saved-name binding so a future save under the same name
      // is treated as a fresh record (won't try to delete a non-existent
      // previous version on rename).
      const nextSavedNames = { ...state.pipelineSavedNames }
      delete nextSavedNames[pipelineId]

      const remaining = state.transformPipelines.filter(
        (p) => p.id !== pipelineId
      )
      // Cross-slice write: rebinding the builder slice's builders is
      // deliberate — slices share one store state.
      // Always keep at least one pipeline so the UI has something to
      // edit. Remaking a fresh "Default" if the user nuked the last.
      if (remaining.length === 0) {
        const fresh = blankPipeline("Default")
        return {
          transformPipelines: [fresh],
          activeTransformPipelineId: fresh.id,
          // Builders pointing at the removed pipeline get rebound to
          // the new default — preserves "apply transforms" intent.
          builders: state.builders.map((b) =>
            b.transformPipelineId === pipelineId
              ? { ...b, transformPipelineId: fresh.id }
              : b
          ),
          pipelineSavedNames: nextSavedNames,
        }
      }
      const nextActive =
        state.activeTransformPipelineId === pipelineId
          ? remaining[remaining.length - 1].id
          : state.activeTransformPipelineId
      return {
        transformPipelines: remaining,
        activeTransformPipelineId: nextActive,
        // Builders that pointed at the removed pipeline lose their
        // transform binding (null = no transforms). Better than
        // silently routing them to a different pipeline.
        builders: state.builders.map((b) =>
          b.transformPipelineId === pipelineId
            ? { ...b, transformPipelineId: null }
            : b
        ),
        pipelineSavedNames: nextSavedNames,
      }
    }),
  renameTransformPipeline: (pipelineId, name) =>
    set((state) => ({
      transformPipelines: state.transformPipelines.map((p) =>
        p.id === pipelineId
          ? { ...p, name: name.trim() || p.name }
          : p
      ),
    })),
  duplicateTransformPipeline: (pipelineId) => {
    let createdId = ""
    set((state) => {
      const source = state.transformPipelines.find(
        (p) => p.id === pipelineId
      )
      if (!source) return {}
      const copy: TransformPipeline = {
        id: newId(),
        name: `${source.name} (copy)`,
        // Recursive id rewrite — top-level steps AND any nested loop
        // body steps get fresh ids so neither copy aliases the other.
        steps: cloneStepsWithFreshIds(source.steps),
      }
      createdId = copy.id
      return {
        transformPipelines: [...state.transformPipelines, copy],
        activeTransformPipelineId: copy.id,
      }
    })
    return createdId
  },
  setActiveTransformPipeline: (pipelineId) =>
    set({ activeTransformPipelineId: pipelineId }),

  applyHydratedPipelines: (incoming) =>
    set((state) => {
      if (incoming.length === 0) return state
      // Merge by name: existing pipelines whose name matches a hydrated
      // one keep their local id (so any builder/active references stay
      // valid); the steps are replaced wholesale by the backend version.
      // Pipelines with no name match get appended as new entries.
      const byName = new Map(
        state.transformPipelines.map((p) => [p.name, p])
      )
      const updated: TransformPipeline[] = []
      const handledNames = new Set<string>()
      for (const remote of incoming) {
        handledNames.add(remote.name)
        // Backend sidecars bypass the Zustand persist `migrate` step,
        // so any schema drift between client versions has to be patched
        // up here. `normalizeHydratedSteps` recursively backfills
        // missing/renamed fields on every loop step in the tree.
        const normalisedSteps = normalizeHydratedSteps(remote.steps)
        const existing = byName.get(remote.name)
        if (existing) {
          updated.push({ ...existing, steps: normalisedSteps })
        } else {
          updated.push({ ...remote, id: newId(), steps: normalisedSteps })
        }
      }
      // Preserve any local pipelines that haven't been pushed to backend yet.
      for (const local of state.transformPipelines) {
        if (!handledNames.has(local.name)) {
          updated.push(local)
        }
      }
      // Active id: keep if its pipeline still exists, else fall back to first.
      const activeStillExists = updated.some(
        (p) => p.id === state.activeTransformPipelineId
      )
      const activeTransformPipelineId = activeStillExists
        ? state.activeTransformPipelineId
        : updated[0]?.id ?? null
      // Mark every hydrated pipeline as confirmed-saved.
      const savedNames: Record<string, string> = {
        ...state.pipelineSavedNames,
      }
      updated.forEach((p) => {
        if (handledNames.has(p.name)) savedNames[p.id] = p.name
      })
      return {
        transformPipelines: updated,
        activeTransformPipelineId,
        pipelineSavedNames: savedNames,
      }
    }),

  markPipelineSaved: (pipelineId, savedName) =>
    set((state) => ({
      pipelineSavedNames: {
        ...state.pipelineSavedNames,
        [pipelineId]: savedName,
      },
    })),
})
